/* eslint-disable max-lines -- the app half of the Work dispatcher: Evidence's trail feed and cards, the timeline's leaves and the two approval hosts, bound once for the package's dispatcher */
import { DraftNoteBody } from '@/components/ui/work-tool-cards/DraftNoteBody';
import { ChatObjectList } from '@/composition/hosts/chat-object-list';
import {
  stripCiteTags,
  stripImgTags,
} from '@/features/projects/components/evidence-subagent/citation-tokens/resolve';
import { cancelComputerUseRun } from '@/features/rpa/agent-client/cancel-computer-use-run';
import { CloudComputerInlineLiveView } from '@/features/rpa/cua-cloud/CloudComputerInlineLiveView';
import { desktopControlApprovalPort } from '@/features/rpa/desktop-control-approval';
import { DesktopControlApprovalWarning } from '@/features/rpa/desktop-control-approval-warning';
import { DESKTOP_TARGETS_QUERY_KEY } from '@/features/rpa/desktop-target/desktopTargets';
import { useComputerUseQueuedTarget } from '@/features/rpa/desktop-target/useComputerUseQueuedTarget';
import { ProtectedActionApproval } from '@/features/rpa/protected-action-approval';
import { useTemplateCreationApprovalGuard } from '@/features/templateBuilder/hooks/forms/useTemplateCreationApprovalGuard';
import { CreateWorkflowApprovalCard } from '@/features/workflows/components/WorkflowApprovalCards';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { trackWorkEvent, WorkLaunchEventName } from '@/models/analytics/features/work';
import { reactQueryClient } from '@/utils/reactQueryClient';
import type {
  ComputerUseToolUseHost,
  ConnectRequiredBlockProps,
  EvidenceSearch,
  EvidenceSource,
  EvidenceStep,
  EvidenceTrail,
  EvidenceTrails,
  InlineApprovalRendererHost,
  ProjectItemRendererDeps as ProjectItemRendererCoreDeps,
  ProjectItemRendererHost,
} from '@heidi/agents';
import { createProjectItemRenderer as createProjectItemRendererCore } from '@heidi/agents';
import type {
  ChatItem,
  PublicRunSnapshot,
  ToolUseItem,
  WorkLaunchAnalyticsProps,
} from '@heidi/agents/contracts';
import { agentHarnessKeys } from '@heidi/agents/contracts';
import type { WrittenObject } from '@heidi/agents/data';
import {
  cancelChildRun,
  EMPTY_TRANSCRIPT_STORE,
  foldTranscript,
  friendlyToolError,
  ingestSnapshot,
  isTerminalRunStatus,
  projectRunFailureMessages,
  readEvidenceSessionInput,
  toBlockStatus,
} from '@heidi/agents/data';
import { isSuppressedEvidenceChildTool } from '@heidi/evidence';
import { deduplicateSources, type Source } from '@heidi/evidence/contracts';
import { useQueryPatientProfileById } from '@heidi/patients/data';
import { hashKey } from '@tanstack/react-query';
import { getDefaultStore } from 'jotai';
import type { ReactNode } from 'react';
import { ApprovalInputDetails } from '../components/approval-input-details';
import { ComputerUseRecordedWorkflowPlan } from '../components/ComputerUseSkillWorkflowPlan';
import { ConnectRequiredBlock } from '../components/ConnectRequiredBlock';
import {
  LIBRARY_SEARCH_TOOL,
  readQuery,
  readSources,
  toSearchStatus,
  WEB_STYLE_SEARCH_TOOLS,
} from '../components/evidence-subagent/evidenceChildData';
import { EvidenceChildRunInline } from '../components/evidence-subagent/EvidenceChildRunInline';
import { EvidenceSessionInlineBlock } from '../components/EvidenceSessionInlineBlock';
import { InlineClarificationBlock } from '../components/InlineClarificationBlock';
import { artifactFromToolUse, toolResultText } from '../harness/artifactFromTool';
import { readRpaSkillWorkflowFromMetadata } from '../lib/read-rpa-skill-workflow';
import { trackWorkRpaDesktopRequiredShown } from '../lib/track-work-rpa-desktop-required-shown';
import {
  buildEvidenceSessionFromPayload,
  readEvidenceRenderPayload,
} from '../state/evidence-render-payload';
import { evidenceSessionsAtom } from '../state/evidence-sessions';
import { renderEvidenceToolUse } from './evidence-tool-adapters';
import { ClinicalTaskToolBlock } from './evidence-tool-adapters/ClinicalTaskToolBlock';
import {
  AssistantText,
  ErrorRow,
  ResolvedClarificationsCard,
  ThinkingText,
  TurnStoppedNote,
  UserBubble,
} from './projectTimelineBlocks';

// What the package's timeline renderers take from this dispatcher: the connect
// card bound to the integrations buttons, the assistant text block, and the
// chat object list. Module scope, so each keeps one identity.
const renderConnectRequired = (props: ConnectRequiredBlockProps): ReactNode => (
  <ConnectRequiredBlock {...props} />
);
const renderSettledAssistantText = (text: string): ReactNode => (
  <AssistantText text={text} streaming={false} />
);
// The Verify child timeline streams, so its prose takes the live flag too.
const renderAssistantText = (text: string, streaming: boolean): ReactNode => (
  <AssistantText text={text} streaming={streaming} />
);
const renderChatObjectList = (objects: WrittenObject[]): ReactNode => (
  <ChatObjectList objects={objects} />
);

// The main window's approval surface for the template quality attestation.
const useAskHeidiApprovalGuard = () =>
  useTemplateCreationApprovalGuard('ask_heidi_approval');

// The Work approval emit, which both approval hosts below report through.
const trackWorkApprovalActioned = (props: WorkLaunchAnalyticsProps): void =>
  trackWorkEvent(WorkLaunchEventName.WorkApprovalActioned, props);

// What the package's inline approval dispatcher takes from the app.
const INLINE_APPROVAL_HOST: InlineApprovalRendererHost = {
  renderDraftBody: ({ body, isStreaming, comparison }) => (
    <DraftNoteBody markdown={body} isStreaming={isStreaming} comparison={comparison} />
  ),
  renderConnectRequired,
  renderRoutineApproval: (props) => <CreateWorkflowApprovalCard {...props} />,
  renderTaskPreview: ({ tasks, title }) => (
    <ClinicalTaskToolBlock status="proposed" tasks={tasks} title={title} />
  ),
  renderTurnStoppedNote: (label) => <TurnStoppedNote label={label} />,
  onTrack: trackWorkApprovalActioned,
  desktopControl: desktopControlApprovalPort,
  useApprovalGuard: useAskHeidiApprovalGuard,
  renderInputDetails: (approval) => <ApprovalInputDetails approval={approval} />,
  patientContext: { useAuth: useIsAuthenticated, useProfile: useQueryPatientProfileById },
};

// What the package's computer-use card takes from the app: the rpa root's run
// control, live view, desktop-busy warning and protected-action checkpoint, the
// clarification card bound to the app's pickers, and the recorded routine's plan,
// which is the recorder's presentation.
const COMPUTER_USE_HOST: ComputerUseToolUseHost = {
  runControl: {
    // The roster is rpa's own cache identity, refreshed on the client the app's
    // provider holds once the occupying run is stopped.
    cancelRun: (agentRunId) => {
      cancelComputerUseRun(agentRunId);
      void reactQueryClient.invalidateQueries({ queryKey: DESKTOP_TARGETS_QUERY_KEY });
    },
    useQueuedTarget: (args) => {
      const target = useComputerUseQueuedTarget(args);
      return target
        ? {
            displayName: target.display_name,
            busyAgentRunId: target.busy_agent_run_id ?? undefined,
          }
        : null;
    },
  },
  renderLiveView: (childAgentRunId) => (
    <CloudComputerInlineLiveView childAgentRunId={childAgentRunId} />
  ),
  renderBlockedWarning: (message, className) => (
    <DesktopControlApprovalWarning message={message} className={className} />
  ),
  ClarificationBlock: InlineClarificationBlock,
  ProtectedActionApproval,
  onTrackDesktopRequiredShown: trackWorkRpaDesktopRequiredShown,
  desktopControl: desktopControlApprovalPort,
  onTrack: trackWorkApprovalActioned,
  readRecordedWorkflow: (metadata, inputMerge, options) => {
    const workflow = readRpaSkillWorkflowFromMetadata(metadata, inputMerge, options);
    if (!workflow) return undefined;
    return ({ nested, defaultExpanded }) => (
      <ComputerUseRecordedWorkflowPlan
        workflow={workflow}
        nested={nested}
        defaultExpanded={defaultExpanded}
      />
    );
  },
};

// A child run's Stop runs outside a render, so it cancels on the app's client.
const cancelChildRunOnAppClient = (childAgentRunId: string): Promise<void> =>
  cancelChildRun(childAgentRunId, reactQueryClient);

// Evidence's card for the tools its adapters own.
const renderEvidenceCard = (
  item: ToolUseItem,
  deps: ProjectItemRendererCoreDeps
): ReactNode =>
  renderEvidenceToolUse(item, deps.intl, {
    onOpenSession: (sessionId) => deps.onOpenArtifact({ type: 'session', sessionId }),
    onOpenPresentation: (presentationId) =>
      deps.onOpenArtifact({ type: 'presentation', presentationId }),
    onOpenDocumentExport: (payload) =>
      deps.onOpenArtifact({ type: 'document-export', ...payload }),
    onOpenArtifact: deps.onOpenArtifact,
  });

// Evidence's sub-agent card. A child run's card streams the child and publishes
// what it folds to the trail feed below; a bridged step renders its session.
const renderEvidenceSubagent = (
  item: ToolUseItem,
  deps: ProjectItemRendererCoreDeps
): ReactNode => {
  const status = toBlockStatus(item.status);
  const input = readEvidenceSessionInput(item.input);
  // Inside a Working block the step's own row lists its searches and sources;
  // a failed or cancelled call keeps its plain row, so its card keeps them.
  const searchesInWorkingBlock =
    deps.workingBlocks?.has(item.id) === true &&
    item.status !== 'error' &&
    item.status !== 'cancelled';
  if (item.childAgentRunId) {
    return (
      <EvidenceChildRunInline
        childAgentRunId={item.childAgentRunId}
        status={status}
        question={input.question}
        allowMount={deps.allowChildStreams}
        searchesInWorkingBlock={searchesInWorkingBlock}
        renderReplyActions={deps.inlineReplyActions?.get(item.id)}
        onLiveItems={publishLiveChildRun}
      />
    );
  }
  return (
    <EvidenceSessionInlineBlock
      toolCallId={item.id}
      status={status}
      question={input.question}
      errorMessage={
        status === 'error'
          ? friendlyToolError(
              toolResultText(item),
              deps.intl.formatMessage(projectRunFailureMessages.infraToolError)
            )
          : undefined
      }
      resultProviderMetadata={item.result_provider_metadata}
      onOpenSession={(sessionId) => deps.onOpenArtifact({ type: 'session', sessionId })}
      onOpenPresentation={(presentationId) =>
        deps.onOpenArtifact({ type: 'presentation', presentationId })
      }
      searchesInWorkingBlock={searchesInWorkingBlock}
    />
  );
};

type EvidenceTimelineEntry = NonNullable<
  ReturnType<typeof buildEvidenceSessionFromPayload>
>['timeline'][number];

type WebSearchEntry = Extract<EvidenceTimelineEntry, { type: 'web_search_item' }>;

type LibrarySearchEntry = Extract<EvidenceTimelineEntry, { type: 'library_search_item' }>;

/**
 * Contiguous web searches form one group — the same rule the Evidence card
 * uses for its "N sources found" summary (`EvidenceTimelineBody`).
 */
const webSearchGroups = (
  timeline: readonly EvidenceTimelineEntry[]
): WebSearchEntry[][] => {
  const groups: WebSearchEntry[][] = [];
  let current: WebSearchEntry[] | null = null;
  for (const entry of timeline) {
    if (entry.type !== 'web_search_item') {
      current = null;
      continue;
    }
    if (!current) {
      current = [];
      groups.push(current);
    }
    current.push(entry);
  }
  return groups;
};

type EvidenceSourceEntry = WebSearchEntry['sources'][number];

// Evidence's entries are built from unvalidated chunks and tool results, streamed
// and replayed alike, so each field is read for what it is.
const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const isEntry = (value: unknown): value is EvidenceSourceEntry =>
  typeof value === 'object' && value !== null;

const isSourceEntry = (value: unknown): value is EvidenceSourceEntry =>
  isEntry(value) && typeof value.url === 'string' && value.url !== '';

const toEvidenceSource = (source: EvidenceSourceEntry): EvidenceSource => ({
  id: source.url,
  url: source.url,
  title: text(source.title) ?? text(source.display_name),
  domain: text(source.domain),
  logo: text(source.logo_url),
});

/**
 * Evidence's id for a library document. A bridged step's saved sources carry
 * it; a child run's carry only the search's id for it, `user-doc-{document_id}`
 * (ml-scribe `domains/evidence/schemas.py` `Source`).
 */
const USER_DOC_PREFIX = 'user-doc-';

const libraryDocumentId = (entry: EvidenceSourceEntry): string | undefined => {
  const sourceId = text(entry.source_id);
  return (
    text(entry.document_id) ??
    (sourceId?.startsWith(USER_DOC_PREFIX)
      ? text(sourceId.slice(USER_DOC_PREFIX.length))
      : undefined)
  );
};

/** A source as the Working row lists it, and the full Evidence source that opens it. */
type FoundSource = [EvidenceSource & { id: string }, EvidenceSourceEntry];

const webSource = (entry: EvidenceSourceEntry): FoundSource => [
  { ...toEvidenceSource(entry), id: entry.url },
  entry,
];

/**
 * What a library search found: one of the clinician's documents, which opens
 * in-app as Evidence's own list opens it, or a page from one of their web
 * collections, which opens like any web page.
 */
const librarySource = (entry: unknown): FoundSource[] => {
  if (!isEntry(entry)) return [];
  const documentId = libraryDocumentId(entry);
  if (documentId) {
    return [
      [
        {
          id: `library:${documentId}`,
          url: '',
          title: text(entry.title),
          fromLibrary: true,
        },
        { ...entry, type: 'user_document', document_id: documentId },
      ],
    ];
  }
  if (!isSourceEntry(entry)) return [];
  const [source, origin] = webSource(entry);
  return [[{ ...source, fromLibrary: true }, origin]];
};

/** Each source once, where it was first found. */
const firstOfEach = (found: readonly FoundSource[]): FoundSource[] => {
  const seen = new Set<string>();
  return found.filter(([{ id }]) => {
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

/** One search as either Evidence path records it. */
interface SearchFacts {
  query: string | undefined;
  done: boolean;
  sources: readonly EvidenceSourceEntry[];
}

/**
 * What the Working row shows of an Evidence step, and the full Evidence source
 * behind each source it lists, by that source's id: opening one needs it.
 */
interface StepSources {
  trail: EvidenceTrail;
  origins: ReadonlyMap<string, EvidenceSourceEntry>;
}

/**
 * A step's trail from its search groups and what its library searches found.
 * Only a completed step carries what it found: every source its searches
 * returned, duplicate URLs removed, then what came from the library, each once.
 */
const trailFrom = (
  groups: readonly (readonly SearchFacts[])[],
  library: readonly unknown[],
  completed: boolean
): StepSources => {
  const searches = groups.flat();
  const found = completed
    ? firstOfEach([
        ...deduplicateSources(searches.flatMap((search) => [...search.sources])).map(
          webSource
        ),
        ...library.flatMap(librarySource),
      ])
    : [];
  return {
    trail: {
      searches: searches.flatMap(({ query, done }): EvidenceSearch[] =>
        query ? [{ query, done }] : []
      ),
      returned: searches.flatMap((search) => search.sources).map(toEvidenceSource),
      found: completed ? found.map(([source]) => source) : null,
    },
    origins: new Map(found.map(([source, entry]) => [source.id, entry])),
  };
};

const bridgeSearch = (entry: WebSearchEntry): SearchFacts => ({
  query: text(entry.query),
  done: entry.status === 'completed',
  sources: Array.isArray(entry.sources) ? entry.sources.filter(isSourceEntry) : [],
});

/**
 * A bridged step's trail from its Evidence session. The grouping rule is
 * re-stated here rather than shared, to leave the Evidence card untouched; the
 * renderer tests pin the two to the same result.
 */
const bridgeTrail = (
  timeline: readonly EvidenceTimelineEntry[],
  completed: boolean
): StepSources =>
  trailFrom(
    webSearchGroups(timeline).map((group) => group.map(bridgeSearch)),
    timeline
      .filter(
        (entry): entry is LibrarySearchEntry => entry.type === 'library_search_item'
      )
      .flatMap((entry): unknown[] => (Array.isArray(entry.sources) ? entry.sources : [])),
    completed
  );

/**
 * The child card's search groups: contiguous web-style searches, stepping over
 * tools it never shows. Residual tools count as shown, so a group splits
 * wherever the card could split it, and a count is only ever one it shows too.
 */
const childSearchGroups = (items: readonly ChatItem[]): ToolUseItem[][] => {
  const groups: ToolUseItem[][] = [];
  let current: ToolUseItem[] | null = null;
  for (const item of items) {
    if (item.kind === 'tool_use' && WEB_STYLE_SEARCH_TOOLS.has(item.name)) {
      if (!current) {
        current = [];
        groups.push(current);
      }
      current.push(item);
    } else if (
      !(item.kind === 'tool_use' && isSuppressedEvidenceChildTool(item.name, true))
    ) {
      current = null;
    }
  }
  return groups;
};

const childSearch = (item: ToolUseItem): SearchFacts => ({
  query: text(readQuery(item)),
  done: toSearchStatus(item.status) === 'completed',
  sources: readSources(item),
});

const childTrail = (items: readonly ChatItem[], completed: boolean): StepSources =>
  trailFrom(
    childSearchGroups(items).map((group) => group.map(childSearch)),
    items
      .filter(
        (item): item is ToolUseItem =>
          item.kind === 'tool_use' && item.name === LIBRARY_SEARCH_TOOL
      )
      .flatMap(readSources),
    completed
  );

/**
 * Each child run's transcript as its Evidence card streams it. The card is the
 * only reader of the child's stream, so it publishes what it folds here, and
 * the step's row follows it without a request of its own.
 */
const liveChildRuns = new Map<string, readonly ChatItem[]>();
const liveChildWatchers = new Map<string, Set<() => void>>();

/**
 * The last trail read from each child's live transcript. The card publishes on
 * every streamed event, the answer's text included, so a trail with the same
 * contents keeps its object and the row doesn't redraw for a token it never
 * shows.
 */
const liveChildTrails = new Map<string, { key: string; sources: StepSources }>();

const publishLiveChildRun = (
  childAgentRunId: string,
  items: readonly ChatItem[] | null
): void => {
  if (items) {
    liveChildRuns.set(childAgentRunId, items);
  } else {
    liveChildRuns.delete(childAgentRunId);
    liveChildTrails.delete(childAgentRunId);
  }
  liveChildWatchers.get(childAgentRunId)?.forEach((onChange) => onChange());
};

const liveChildTrail = (
  childAgentRunId: string,
  items: readonly ChatItem[],
  completed: boolean
): StepSources | null => {
  const sources = cachedTrail(items, completed, () => childTrail(items, completed));
  if (!sources) return null;
  const key = JSON.stringify(sources.trail);
  const last = liveChildTrails.get(childAgentRunId);
  if (last?.key === key) return last.sources;
  liveChildTrails.set(childAgentRunId, { key, sources });
  return sources;
};

/**
 * Trails cached on the object they were read from, which Evidence replaces
 * whenever the step's data moves: a saved result's metadata (the transcript
 * fold rebuilds every item, while an unchanged result keeps its object), a live
 * session, or a child run's snapshot.
 */
const trailCache = new WeakMap<
  object,
  Partial<Record<'running' | 'completed', StepSources | null>>
>();

const cachedTrail = (
  source: object,
  completed: boolean,
  build: () => StepSources | null
): StepSources | null => {
  const cached = trailCache.get(source);
  const key = completed ? 'completed' : 'running';
  if (cached && key in cached) return cached[key] ?? null;
  const sources = build();
  trailCache.set(source, { ...cached, [key]: sources });
  return sources;
};

/** A bridged step's trail from the session saved on its result, if that renders. */
const savedSources = (step: EvidenceStep): StepSources | null => {
  const completed = step.status === 'complete';
  const metadata = step.result_provider_metadata;
  return metadata
    ? cachedTrail(metadata, completed, () => {
        const payload = readEvidenceRenderPayload(metadata);
        const session = payload && buildEvidenceSessionFromPayload(payload, step.id);
        return session ? bridgeTrail(session.timeline, completed) : null;
      })
    : null;
};

const readEvidenceSources = (step: ToolUseItem): StepSources | null => {
  const completed = step.status === 'complete';
  if (step.childAgentRunId) {
    const live = liveChildRuns.get(step.childAgentRunId);
    if (live) return liveChildTrail(step.childAgentRunId, live, completed);
    const run = reactQueryClient.getQueryData<PublicRunSnapshot>(
      agentHarnessKeys.run(step.childAgentRunId)
    );
    return run
      ? cachedTrail(run, completed, () =>
          childTrail(
            foldTranscript(ingestSnapshot(EMPTY_TRANSCRIPT_STORE, run)),
            completed
          )
        )
      : null;
  }
  // The card's rule (`EvidenceSessionInlineBlock`): the saved session wins, and
  // the live one stands in while the step runs, or once it has entries when
  // nothing renderable was saved.
  const saved = savedSources(step);
  if (saved) return saved;
  const live = getDefaultStore().get(evidenceSessionsAtom).get(step.id);
  return live && (!completed || live.timeline.length)
    ? cachedTrail(live, completed, () => bridgeTrail(live.timeline, completed))
    : null;
};

const readEvidenceTrail = (step: ToolUseItem): EvidenceTrail | null =>
  readEvidenceSources(step)?.trail ?? null;

/**
 * Watch a child run for its step's row. The row follows what the Evidence card
 * streams; with no card publishing (a nested renderer, or a transcript still
 * loading) it reads the cached snapshot. That snapshot was fetched as the child
 * appeared, so a settled step whose copy still reads as running refetches it
 * once: it may predate the child's final searches.
 */
const watchChildRun = (
  childAgentRunId: string,
  settled: boolean,
  onChange: () => void
): (() => void) => {
  const runKey = agentHarnessKeys.run(childAgentRunId);
  const runHash = hashKey(runKey);
  const watchers = liveChildWatchers.get(childAgentRunId) ?? new Set<() => void>();
  watchers.add(onChange);
  liveChildWatchers.set(childAgentRunId, watchers);
  const unsubscribe = reactQueryClient.getQueryCache().subscribe((event) => {
    if (event.query.queryHash === runHash) onChange();
  });
  const run = reactQueryClient.getQueryData<PublicRunSnapshot>(runKey);
  if (
    settled &&
    !liveChildRuns.has(childAgentRunId) &&
    run &&
    !isTerminalRunStatus(run.status)
  ) {
    void reactQueryClient.refetchQueries(
      { queryKey: runKey, exact: true },
      { cancelRefetch: false }
    );
  }
  return () => {
    unsubscribe();
    watchers.delete(onChange);
    if (watchers.size === 0) liveChildWatchers.delete(childAgentRunId);
  };
};

/**
 * Evidence runs a step one of two ways. A child run keeps its own transcript,
 * which its card streams; a bridged step streams into Evidence's session map,
 * then saves its session on the result. A settled bridged step whose saved
 * session renders never changes again, so only one that fell back to the live
 * session keeps watching the map.
 */
const subscribeEvidenceTrail: EvidenceTrails['subscribe'] = (step, onChange) => {
  if (step.childAgentRunId) {
    return watchChildRun(step.childAgentRunId, step.status === 'complete', onChange);
  }
  if (step.status === 'complete' && savedSources(step)) return () => undefined;
  return getDefaultStore().sub(evidenceSessionsAtom, onChange);
};

/**
 * The Working block's Evidence port. Its reads are module functions, so the
 * row's subscriptions survive this renderer being rebuilt; opening a source
 * looks up the full Evidence source by the id the row lists it under.
 */
const evidenceTrailsFor = (
  openEvidenceSource: ProjectItemRendererDeps['openEvidenceSource']
): EvidenceTrails => ({
  subscribe: subscribeEvidenceTrail,
  read: readEvidenceTrail,
  openSource: openEvidenceSource
    ? (step, source, citationNumber) => {
        const origin = readEvidenceSources(step)?.origins.get(source.id ?? source.url);
        if (origin) openEvidenceSource(origin, citationNumber);
      }
    : undefined,
});

// The dispatcher's app half, one object so every slot keeps one identity.
const PROJECT_ITEM_RENDERER_HOST: ProjectItemRendererHost = {
  UserBubble,
  AssistantText,
  ThinkingText,
  ErrorRow,
  TurnStoppedNote,
  ResolvedClarificationsCard,
  stripAnswerTokens: (text, { streaming }) =>
    stripImgTags(stripCiteTags(text, { streaming }), { streaming }),
  renderSettledAssistantText,
  renderAssistantText,
  renderConnectRequired,
  renderChatObjectList,
  resolveArtifact: artifactFromToolUse,
  renderEvidenceCard,
  renderEvidenceSubagent,
  inlineApproval: INLINE_APPROVAL_HOST,
  computerUse: COMPUTER_USE_HOST,
  cancelChildRun: cancelChildRunOnAppClient,
};

/**
 * The dispatcher's deps as the timeline hands them over: Evidence's source
 * opener in place of the Working block's Evidence port, which is built from it
 * here, once per renderer.
 */
export interface ProjectItemRendererDeps extends Omit<
  ProjectItemRendererCoreDeps,
  'evidenceTrails'
> {
  /**
   * Opens one of an Evidence step's sources through Evidence's routing (viewer,
   * library, new tab) and reports the click. The page binds it once; without it
   * a Working row only names its sources.
   */
  openEvidenceSource?: (source: Source, citationNumber: number) => void;
}

/** The Work timeline's `renderItem`, with the app's half of the dispatcher bound. */
export const createProjectItemRenderer = ({
  openEvidenceSource,
  ...deps
}: ProjectItemRendererDeps) =>
  createProjectItemRendererCore(
    { ...deps, evidenceTrails: evidenceTrailsFor(openEvidenceSource) },
    PROJECT_ITEM_RENDERER_HOST
  );
